import { useCallback, useEffect, useState } from 'react';

/**
 * Les quatre apparences de Colette (handoff Horizon).
 *
 * `auto` n'est pas une couleur mais une regle : nuit de 19 h a 7 h, jour sinon.
 * Elle se resout DANS LE FUSEAU DU FOYER, jamais dans celui de l'appareil — le premier
 * testeur vit a Dakar et se deplace ; un theme qui basculerait sur l'heure du telephone
 * ferait la nuit en plein apres-midi apres un vol.
 */
export type Theme = 'auto' | 'jour' | 'nuit' | 'blanc';

const CLE = 'colette.theme';
const ORDRE: Theme[] = ['auto', 'jour', 'nuit', 'blanc'];

function estUnTheme(valeur: string | null): valeur is Theme {
  return valeur !== null && (ORDRE as string[]).includes(valeur);
}

function lireChoix(): Theme {
  try {
    const brut = window.localStorage.getItem(CLE);
    return estUnTheme(brut) ? brut : 'auto';
  } catch {
    // Navigation privee, stockage refuse : l'application doit s'ouvrir quand meme.
    return 'auto';
  }
}

/**
 * L'heure (0-23) d'un instant dans un fuseau.
 *
 * Par `formatToParts` et `hourCycle: 'h23'`, jamais par `format()` : le format francais
 * rend « 24 » a minuit sur certains moteurs et « 07 h 10 » sur d'autres — deux pieges
 * deja payes dans ce depot.
 */
function heureDans(instant: Date, fuseau: string): number {
  const parties = new Intl.DateTimeFormat('fr-FR', {
    timeZone: fuseau,
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  return Number(parties.find((p) => p.type === 'hour')?.value ?? '12');
}

/** La nuit court de 19 h a 7 h. Exportee pour etre testable sans navigateur. */
export function resoudre(choix: Theme, fuseau: string, maintenant: Date): 'jour' | 'nuit' | 'blanc' {
  if (choix !== 'auto') return choix;
  const heure = heureDans(maintenant, fuseau);
  return heure >= 19 || heure < 7 ? 'nuit' : 'jour';
}

/** Ce que montre la pastille : « Auto · nuit », « Nuit », « Blanc »… */
export function libelleDuTheme(choix: Theme, resolu: 'jour' | 'nuit' | 'blanc'): string {
  if (choix === 'auto') return `Auto · ${resolu}`;
  return choix.charAt(0).toUpperCase() + choix.slice(1);
}

export function useTheme(fuseau: string) {
  const [choix, setChoix] = useState<Theme>(lireChoix);
  // L'heure entre dans l'etat pour que le theme `auto` bascule tout seul a 19 h sans
  // qu'on ait besoin de recharger la page.
  const [maintenant, setMaintenant] = useState(() => new Date());

  useEffect(() => {
    const minuteur = window.setInterval(() => setMaintenant(new Date()), 60_000);
    return () => window.clearInterval(minuteur);
  }, []);

  const resolu = resoudre(choix, fuseau, maintenant);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolu);
  }, [resolu]);

  const changer = useCallback((nouveau: Theme) => {
    setChoix(nouveau);
    try {
      window.localStorage.setItem(CLE, nouveau);
    } catch {
      // Le choix vaudra pour cette session seulement : mieux que de refuser le clic.
    }
  }, []);

  const cycler = useCallback(() => {
    setChoix((actuel) => {
      const suivant = ORDRE[(ORDRE.indexOf(actuel) + 1) % ORDRE.length]!;
      try {
        window.localStorage.setItem(CLE, suivant);
      } catch {
        // Idem.
      }
      return suivant;
    });
  }, []);

  return { choix, resolu, libelle: libelleDuTheme(choix, resolu), changer, cycler };
}
